import * as React from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import ModalLayout from '@/modules/drivers/modals/shared/ModalLayout'
import { Dropdown } from '@/app/features/company-selection/Dropdown'
import { CompanyIcon } from '@/app/features/icons/Company'
import { useQueryCompanies } from '@/modules/shared/query-client'
import { isUnusableServiceCompany } from '@/app/constants/companyType'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'

type Props = {
  companyId: string | null
  ip: string | null
  onCompanyChange: () => void
}

/**
 * Firma ma listę dozwolonych adresów IP, a bieżący adres na niej nie jest (403 IP_NOT_ALLOWED).
 * Użytkownik może mieć dostęp do innych firm, więc zostawiamy przełącznik firmy.
 */
export const IpNotAllowedModal = ({ companyId, ip, onCompanyChange }: Props) => {
  const { t } = useLocalize()
  const location = useLocation()
  const navigate = useNavigate()
  const companiesQuery = useQueryCompanies({ includeDeleted: false })

  const currentCompany = React.useMemo(() => {
    if (!companyId || !companiesQuery.data) return null
    return (
      companiesQuery.data.find((c) => c.id?.toLowerCase() === companyId.toLowerCase()) ?? null
    )
  }, [companyId, companiesQuery.data])

  const companiesOptions = (companiesQuery.data ?? [])
    .filter(
      (c) =>
        c.id?.toLowerCase() !== companyId?.toLowerCase() &&
        c.isValid !== false &&
        !isUnusableServiceCompany(c),
    )
    .map((company) => ({
      label: company.companyName || '',
      value: company.id,
      icon: CompanyIcon,
    }))

  const goToCompany = (newCompanyId: string) => {
    onCompanyChange()
    navigate(
      location.pathname.replace(/\/company\/([^/]+)/, `/company/${newCompanyId}`),
    )
  }

  return (
    <ModalLayout>
      <div className="tw-relative tw-flex tw-w-full tw-max-w-[460px] tw-flex-col tw-rounded-lg tw-bg-base-100 tw-p-6 tw-shadow-xl">
        <h2 className="tw-mb-2 tw-text-lg tw-font-semibold tw-text-content-base">
          {t('ipAllowList.blocked-title')}
        </h2>
        <p className="tw-text-sm tw-text-content-secondary">
          {currentCompany?.companyName
            ? t('ipAllowList.blocked-description', { name: currentCompany.companyName })
            : t('ipAllowList.blocked-description-generic')}
        </p>
        {ip && (
          <p className="tw-mt-2 tw-text-sm tw-text-content-secondary">
            {t('ipAllowList.current-ip')}{' '}
            <span className="tw-font-mono tw-text-content-base">{ip}</span>
          </p>
        )}

        {companiesOptions.length > 0 && (
          <div className="tw-mt-6 tw-border-t tw-border-base-300 tw-pt-4">
            <p className="tw-mb-2 tw-text-xs tw-text-content-subtle">
              {t('ipAllowList.blocked-switch')}
            </p>
            <Dropdown
              icon={CompanyIcon}
              placeholder={t('shared.sidebar.select-company')}
              options={companiesOptions}
              name="company"
              value={null}
              onChange={goToCompany}
            />
          </div>
        )}
      </div>
    </ModalLayout>
  )
}
